import React, { useState } from 'react';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import Ticket from '../../../interfaces/ticket/Ticket';
import Sprint from '../../../interfaces/sprint/Sprint';
import TicketCard from '../IssuesPage/TicketCard';
import './sprintview.css';
import { updateTicketStatus } from '../../../services/statusServices/StatusService';
import { useAuth } from '../../../firebase/AuthProvider';
import { Button } from 'react-bootstrap';
import CloseSprintModal from './CloseSprintModal';
import { closeSprint } from '../../../services/sprintServices/SprintService';
import { BOARD_COLUMNS, getStatusesForColumn } from '../../../utilities/statusUtils';
import TicketStatus from '../../../interfaces/ticket/TicketStatus';
import { useNotifications } from '../../../contexts/NotificationProvider';

interface SprintBoardProps {
    tickets: Ticket[];
    statuses: TicketStatus[];
    sprint: Sprint | undefined;
    onTicketStatusChange: (ticketId: string, newStatus: string) => void;
    refreshData: () => void;
    onTicketClick: (ticketId: string) => void;
}

const SprintBoard: React.FC<SprintBoardProps> = ({ tickets, statuses, sprint, onTicketStatusChange, refreshData, onTicketClick }) => {
    const { currentUser } = useAuth();
    const [showCloseSprintModal, setShowCloseSprintModal] = useState(false);
    const { notify } = useNotifications();

    const onDragEnd = async (result: DropResult) => {
        const { source, destination, draggableId } = result;
        if (!destination) return;

        const ticketId = draggableId;
        const newStatusName = destination.droppableId;

        if (source.droppableId === newStatusName && source.index === destination.index) {
            return;
        }

        // Optimistic UI update
        onTicketStatusChange(ticketId, newStatusName);

        if (currentUser) {
            const token = await currentUser.getIdToken();
            const success = await updateTicketStatus(token, ticketId, newStatusName);
            if (!success) {
                notify(`Failed to update ticket ${ticketId} status.`, { type: 'error' });
                // Revert the optimistic update by refreshing data
                refreshData();
            }
        }
    };

    const handleSprintClose = async (summaryNotes: string, newSprint: { name: string; startDate: string; endDate: string }) => {
        if (currentUser && sprint) {
            const token = await currentUser.getIdToken();
            const result = await closeSprint(token, sprint.id, summaryNotes, newSprint);
            if (result) {
                notify('Sprint closed successfully!', { type: 'success' });
                refreshData();
            } else {
                notify('Failed to close sprint.', { type: 'error' });
            }
        }
    };

    return (
        <div className="sprint-board-container">
            <div className="sprint-board-header">
                <h2>{sprint?.name || 'Active Sprint'}</h2>
                <Button variant="primary" onClick={() => setShowCloseSprintModal(true)}>
                    Close Sprint
                </Button>
            </div>
            <DragDropContext onDragEnd={onDragEnd}>
                <div className="sprint-board">
                    {BOARD_COLUMNS.map(columnName => {
                        const statusesInColumn = getStatusesForColumn(columnName);
                        const ticketsInColumn = tickets.filter(ticket =>
                            statusesInColumn.some(s => s.name === ticket.status)
                        );

                        return (
                            <div key={columnName} className="status-column">
                                <h3>{columnName}</h3>
                                {statusesInColumn.map((status, index) => {
                                    const ticketsForStatus = tickets.filter(t => t.status === status.name);
                                    return (
                                        <Droppable key={status.name} droppableId={status.name}>
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.droppableProps}
                                                    className={`ticket-list-sub-zone ${snapshot.isDraggingOver ? 'dragging-over' : ''} ${statusesInColumn.length > 1 ? 'has-multiple' : ''}`}
                                                >
                                                    {statusesInColumn.length > 1 && (
                                                        <div className="sub-zone-title">{status.name}</div>
                                                    )}
                                                    {ticketsForStatus.map((ticket, ticketIndex) => (
                                                        <TicketCard key={ticket.id} ticket={ticket} index={ticketIndex} sprintView={true} onCardClick={onTicketClick} />
                                                    ))}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </DragDropContext>
            <CloseSprintModal
                show={showCloseSprintModal}
                onHide={() => setShowCloseSprintModal(false)}
                sprintToClose={sprint}
                onSprintClose={handleSprintClose}
            />
        </div>
    );
};

export default SprintBoard;